import { Injectable, inject } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@tolgee/ngx";
import {
	BehaviorSubject,
	Observable,
	Subscription,
	catchError,
	combineLatest,
	exhaustMap,
	filter,
	of,
	switchMap,
	takeWhile,
	timer,
} from "rxjs";
import { AuthService } from "./auth.service";
import { MitpManagerService } from "./mitp-manager.service";
import { NotificationService } from "./notification.service";
import { RemoteGuidanceService } from "./remote-guidance.service";
import { RingingCall } from "../interfaces/remote-guidance";
import { IncomingCallDialogComponent } from "../modules/remote-guidance/incoming-call-dialog/incoming-call-dialog.component";

/** How often an expert's web app asks whether glasses are calling. */
export const POLL_INTERVAL_MS = 3000;

/**
 * Makes the expert's web app ring when the institute's glasses call. Polls while an expert is
 * logged in and not already in a call, and stops for the session if the backend refuses (403:
 * the institute has no remote guidance option).
 */
@Injectable({
	providedIn: "root",
})
export class IncomingCallService {
	private remoteGuidance = inject(RemoteGuidanceService);
	private auth = inject(AuthService);
	private manager = inject(MitpManagerService);
	private dialog = inject(MatDialog);
	private router = inject(Router);
	private toastr = inject(NotificationService);
	private translate = inject(TranslateService);

	/** Calls ringing right now in the expert's institute. */
	readonly calls$ = new BehaviorSubject<RingingCall[]>([]);

	private inCall = new BehaviorSubject(false);
	private forbidden = false;
	private dismissed = new Set<string>();
	private dialogRef: MatDialogRef<IncomingCallDialogComponent, boolean> | null = null;
	private dialogCallId: string | null = null;
	private subscription: Subscription | null = null;

	watch(): void {
		if (this.subscription) {
			return;
		}
		this.subscription = combineLatest([this.auth.authStatus, this.inCall])
			.pipe(
				switchMap(([authenticated, inCall]) => {
					if (!authenticated) {
						this.forbidden = false;
					}
					return authenticated && !inCall && !this.forbidden && this.manager.isExpert()
						? this.poll()
						: of([]);
				})
			)
			.subscribe((calls) => this.update(calls));
	}

	/** Set by the session page: an expert in a call is not rung again. */
	setInCall(inCall: boolean): void {
		this.inCall.next(inCall);
	}

	/** Takes the call and opens its session; another expert may have been faster. */
	answer(call: RingingCall): void {
		this.remoteGuidance.accept(call.id).subscribe({
			next: () => void this.router.navigate(["/remote-guidance", call.id]),
			error: (error: HttpErrorResponse) => {
				this.dismissed.add(call.id);
				this.toastr.warning(
					this.translate.instant(
						error.status === 409
							? "remote-guidance.incoming.already-taken"
							: "remote-guidance.incoming.failed"
					)
				);
			},
		});
	}

	private poll(): Observable<RingingCall[]> {
		return timer(0, POLL_INTERVAL_MS).pipe(
			exhaustMap(() =>
				this.remoteGuidance.ringing().pipe(
					catchError((error: HttpErrorResponse) => {
						if (error.status === 403) {
							this.forbidden = true;
						}
						return of(null);
					})
				)
			),
			takeWhile(() => !this.forbidden),
			filter((calls): calls is RingingCall[] => calls !== null)
		);
	}

	private update(calls: RingingCall[]): void {
		this.calls$.next(calls);
		const ids = new Set(calls.map((c) => c.id));
		this.dismissed.forEach((id) => {
			if (!ids.has(id)) {
				this.dismissed.delete(id);
			}
		});

		// Answered by someone else, cancelled or missed: stop ringing.
		if (this.dialogRef && !ids.has(this.dialogCallId)) {
			this.dialogRef.close();
		}
		if (this.dialogRef) {
			return;
		}

		const next = calls.find((c) => !this.dismissed.has(c.id));
		if (next) {
			this.ring(next);
		}
	}

	private ring(call: RingingCall): void {
		this.dialogCallId = call.id;
		this.dialogRef = this.dialog.open(IncomingCallDialogComponent, { data: call, disableClose: true });
		this.dialogRef.afterClosed().subscribe((answer) => {
			this.dialogRef = null;
			this.dialogCallId = null;
			if (answer === true) {
				this.answer(call);
			} else if (answer === false) {
				// Ignored by the expert. A dialog closed because the call stopped ringing is not.
				this.dismissed.add(call.id);
			}
		});
	}
}
